import * as XLSX from "xlsx";

/* Spreadsheet parsing, validation and record building for bulk athlete upload.
   Shared by BulkUpload (the coach/parent flow) and the admin concierge import,
   so a file an admin imports on a customer's behalf is read exactly the way
   the customer's own upload would have been. */

export const COLUMNS = {
  asCoach: ["first_name", "last_name", "gender", "dob", "email", "parent_email", "code"],
  asParent: ["first_name", "last_name", "gender", "dob", "email", "code"],
};

/* Accepted spellings of each template header, compared after lower-casing and
   stripping everything but letters ("date_of_birth(MM/DD/YYYY)" -> "dateofbirthmmddyyyy"). */
const HEADER_PREFIXES = {
  first_name: ["firstname"],
  last_name: ["lastname"],
  gender: ["gender"],
  dob: ["dateofbirth", "dob", "birthdate"],
  email: ["email"],
  parent_email: ["parentemail"],
  code: ["referralcode", "code"],
};

/* Order in which the instant (≤ MAX_BULK_ROWS) flow reports the first problem
   it finds. Matches the order the checks ran in before they were extracted. */
export const ERROR_PRIORITY = [
  "missingField",
  "invalidGender",
  "invalidDOB",
  "invalidAge",
  "parentEmailForAdult",
  "sameRow",
  "invalidEmail",
  "repeated",
  "sharedParent",
];

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const VALID_GENDERS = ["Male", "Female"];

export const ageFromDate = (birthDate) =>
  Math.abs(new Date(Date.now() - birthDate.getTime()).getUTCFullYear() - 1970);

const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/[^a-z]/g, "");

/* Read the first sheet of an .xlsx ArrayBuffer.
   Returns { headerMatches, rows } where rows are the non-blank data rows keyed
   by COLUMNS[adding], each tagged with its 1-based spreadsheet row number. */
export const readWorkbook = (arrayBuffer, adding) => {
  const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const columns = COLUMNS[adding];
  const headerRow = (XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 0, raw: false, defval: "" })[0] || []).map(normalizeHeader);
  const headerMatches = columns.every((col, i) => HEADER_PREFIXES[col].some((p) => (headerRow[i] || "").startsWith(p)))
    && !headerRow.slice(columns.length).some(Boolean);
  const raw = XLSX.utils.sheet_to_json(worksheet, { header: columns, range: 1, raw: false, defval: "" });
  // Spreadsheets routinely declare a used range larger than the data in them,
  // which yields trailing all-blank rows. Drop those before counting so a valid
  // file is never rejected for rows the user cannot see. sheetRow is taken
  // before filtering so it still points at the right spreadsheet row.
  const rows = raw
    .map((r, i) => ({ ...r, sheetRow: i + 2 }))
    .filter((r) => columns.some((c) => String(r[c] ?? "").trim() !== ""));
  return { headerMatches, rows };
};

/* Validate rows. Returns
     validRows:  rows that passed the per-row checks, normalised
     errors:     [{ type, sheetRow }]   one per failing row (first failure wins)
     duplicates: [{ type, email, rows }] cross-row and same-row email conflicts
   Nothing short-circuits, so the full list can be shown for large files. */
export const validateRows = (rows, adding) => {
  const errors = [];
  const duplicates = [];
  const validRows = [];
  for (const learner of rows) {
    const { sheetRow } = learner;
    const str = (v) => String(v ?? "").trim();
    const first_name = str(learner.first_name), last_name = str(learner.last_name);
    const gender = str(learner.gender), email = str(learner.email), code = str(learner.code);
    const dob = String(learner.dob ?? "");
    const parent_email = adding === "asCoach" ? str(learner.parent_email) : "";
    const fail = (type) => errors.push({ type, sheetRow });

    if (!first_name || !last_name || !gender || !dob.trim()) { fail("missingField"); continue; }
    if (adding === "asCoach" && !email && !parent_email) { fail("missingField"); continue; }
    if (adding !== "asCoach" && !email) { fail("missingField"); continue; }
    if (!VALID_GENDERS.includes(gender)) { fail("invalidGender"); continue; }
    // Accept "/" or "-" as the separator. The template and the instructions both
    // say MM/DD/YYYY, but the instructions said MM-DD-YYYY for a long time and
    // several locales format dates with dashes, so dashed rows keep arriving.
    // The round-trip check below still rejects anything that is not a real date.
    const [month, day, year] = dob.trim().split(/[/-]/).map((part) => part.trim());
    if (!month || !day || !year) { fail("invalidDOB"); continue; }
    if (!/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day) || !/^\d{4}$/.test(year)) { fail("invalidDOB"); continue; }
    const dobIso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    const birthDate = new Date(`${dobIso}T00:00:00Z`);
    if (isNaN(birthDate.getTime()) || birthDate.toISOString().slice(0, 10) !== dobIso) { fail("invalidDOB"); continue; }
    const user_age = ageFromDate(birthDate);
    if ((adding === "asCoach" && user_age < 8) || (adding === "asParent" && (user_age < 8 || user_age > 18))) { fail("invalidAge"); continue; }
    if (adding === "asCoach" && user_age >= 18 && parent_email) { fail("parentEmailForAdult"); continue; }
    if (adding === "asCoach" && parent_email && email && parent_email.toLowerCase() === email.toLowerCase()) {
      duplicates.push({ type: "sameRow", email: email.toLowerCase(), rows: [sheetRow] });
    }
    if ((email && !EMAIL_RE.test(email)) || (parent_email && !EMAIL_RE.test(parent_email))) {
      errors.push({ type: "invalidEmail", sheetRow });
    }
    validRows.push({ ...learner, first_name, last_name, email, gender, dob: dobIso, code, parent_email, dob_display: learner.dob, user_age, sheetRow });
  }

  const emailMap = {};
  for (const l of validRows) {
    if (l.email) (emailMap[l.email.toLowerCase()] = emailMap[l.email.toLowerCase()] || []).push(l.sheetRow);
  }
  Object.entries(emailMap)
    .filter(([, rowList]) => rowList.length > 1)
    .forEach(([email, rowList]) => duplicates.push({ type: "repeated", email, rows: rowList }));

  if (adding === "asCoach") {
    // A shared account logs in with the parent's email, so it can hold one child:
    // that address may not appear anywhere else in the file.
    const sharedParentEmails = [...new Set(validRows.filter((l) => !l.email && l.parent_email).map((l) => l.parent_email.toLowerCase()))];
    const allEmailsFlat = validRows.flatMap((l) => [l.email.toLowerCase() || null, l.parent_email.toLowerCase() || null]).filter(Boolean);
    sharedParentEmails
      .filter((e) => allEmailsFlat.filter((x) => x === e).length > 1)
      .forEach((e) => duplicates.push({
        type: "sharedParent",
        email: e,
        rows: validRows.filter((l) => l.email.toLowerCase() === e || l.parent_email.toLowerCase() === e).map((l) => l.sheetRow),
      }));
  }
  return { validRows, errors, duplicates };
};

/* The highest-priority problem type present, or null when the file is clean. */
export const firstProblemType = ({ errors, duplicates }) => {
  const present = new Set([...errors.map((e) => e.type), ...duplicates.map((d) => d.type)]);
  return ERROR_PRIORITY.find((type) => present.has(type)) || null;
};

/* Every problem, grouped by type, for the concierge warning list and the
   admin import review: [{ type, rows: [sheetRow...], emails: [...] }]. */
export const groupProblems = ({ errors, duplicates }) => {
  const groups = {};
  const group = (type) => (groups[type] = groups[type] || { type, rows: [], emails: [] });
  errors.forEach((e) => group(e.type).rows.push(e.sheetRow));
  duplicates.forEach((d) => { const g = group(d.type); g.rows.push(...d.rows); g.emails.push(d.email); });
  return ERROR_PRIORITY.filter((type) => groups[type]).map((type) => ({ ...groups[type], rows: [...new Set(groups[type].rows)].sort((a, b) => a - b) }));
};

/* Number of distinct spreadsheet rows with at least one problem. */
export const problemRowCount = ({ errors, duplicates }) =>
  new Set([...errors.map((e) => e.sheetRow), ...duplicates.flatMap((d) => d.rows)]).size;

/* Build the learner records POSTed to /users/bulk-upload. `guardian` is the
   coach/parent the learners are added under: the signed-in user in the normal
   flow, the concierge request's uploader in the admin import. */
export const buildLearnerRecords = (rows, adding, guardian) => {
  const learners = [];
  rows.forEach((learner) => {
    const name = learner.first_name.replace(/\s+/g, "_") + " " + learner.last_name.replace(/\s+/g, "_");
    const birthDate = new Date(learner.dob);
    if (isNaN(birthDate.getTime())) return;
    const age = learner.user_age ? learner.user_age : ageFromDate(birthDate);
    const code = learner.code, learnerEmail = learner.email?.trim() || "";
    const parentEmail = adding === "asCoach" ? learner.parent_email?.trim() || "" : "";
    let relationships = { parents: [guardian.id] };
    let account_type = null, emailForRecord = learnerEmail;
    let type = age < 18 ? ["Learner"] : ["Learner", "Adult"];
    let details = { name, dob: learner.dob, persona: ["Athlete"], gender: learner.gender, address: { street: null, city: null, state: null, country: null, zip: null } };
    if (adding === "asCoach") {
      relationships = age < 18 ? { coaches: [guardian.id] } : { coaches: [guardian.id], children: [] };
      if (age < 18 && parentEmail && learnerEmail) { account_type = "separate"; }
      else if (age < 18 && parentEmail && !learnerEmail) {
        account_type = "shared"; type = ["Learner", "Shared"]; emailForRecord = parentEmail;
        details = { name: null, dob: null, gender: null, persona: ["Parent"], address: { street: null, city: null, state: null, country: null, zip: null }, learner_details: { name, dob: learner.dob, persona: ["Athlete"], gender: learner.gender } };
      }
    }
    learners.push({
      email: emailForRecord, ...(account_type === "separate" ? { parent_email: parentEmail } : {}),
      account_type, phone: null, relationships, cognito_user_id: null, type, language: guardian.language || "en",
      is_demo_user: false, status: "inactive", true_mindsets: !!guardian.true_mindsets,
      source: guardian.source ? guardian.source : (code && code.includes("_") ? code.split("_")[0] + "_code" : null),
      details, payment_status: "pending", profile_image_url: null,
      referral_code: code ? code : null,
      identities: [{ google: { id: "" }, facebook: { id: "" }, apple: { id: "" }, amazon: { id: "" }, email: { id: "" } }],
    });
  });
  return learners;
};
